import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import * as apiGet from 'api/originLocation/GetOriginLocationIdV1';
import OriginLocation from 'classes/StandingData/OriginLocation/OriginLocation';
import OriginLocationLanguageInput from 'components/desktop/StandingData/OriginLocation/LanguageInput/OriginLocationLanguageInput';
import BtnSave from 'components/desktop/_general/Button/BtnSave/BtnSave';
import LanguageSwitch from 'components/desktop/_general/LanguageSwitch/LanguageSwitch';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import clone from 'functions/clone';
import { getIdFromUrl } from 'functions/url';
import { setIdToUrl } from 'functions/url';
import { RootState } from 'reducers/rootReducer';

const OriginLocationDetailComponent = () => {
	const { t } = useTranslation();

	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);

	const [selectedCultureCode, setSelectedCultureCode] = useState<string>('');
	const [originLocation, setOriginLocation] = useState<OriginLocation>(new OriginLocation());
	const [id, setId] = useState<string | null>(null);

	useEffect(() => {
		initialize();
		setSelectedCultureCode(reduxCultureCode);
	}, []);

	const initialize = async (): Promise<void> => {
		const id: string | null = getIdFromUrl();
		setId(id);
		if (id !== null && id !== 'new') {
			getOriginLocation(id);
		}
	};

	const getOriginLocation = async (id: string): Promise<void> => {
		setIsLoading(true);
		const response = await apiGet.callApi(id);
		response.do((x) => setOriginLocation(new OriginLocation(x)));
		setIsLoading(false);
	};

	const handleInputValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const name = e.currentTarget.name;
		const value = e.currentTarget.value;
		switch (name) {
			case 'nameTranslations':
				originLocation.nameTranslations[selectedCultureCode] = value;
				break;
			case 'code':
				originLocation.nameTranslations[selectedCultureCode] = value;
				break;
		}
		setOriginLocation(clone(originLocation));
	};

	const handleTranslate = async () => {
		setIsLoading(true);
		await originLocation.callApiTranslation(reduxCultureCode, selectedCultureCode);
		setOriginLocation(clone(originLocation));
		setIsLoading(false);
	};

	const handleCodeValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		originLocation.code = e.currentTarget.value;
		setOriginLocation(clone(originLocation));
	};

	const handleSelectLanguage = (_e: any, selectedLanguage: string) => {
		setSelectedCultureCode(selectedLanguage);
	};

	const handleSaveOriginLocation = async (): Promise<void> => {
		setIsLoadingSave(true);
		try {
			if (id !== null && id !== 'new') {
				await originLocation.callApiPut();
			} else {
				await originLocation.callApiPost();
				if (!originLocation.id) return;
				setId(originLocation.id);
				setIdToUrl(originLocation.id);
			}
		} catch {
			setIsLoadingSave(false);
		} finally {
			setIsLoadingSave(false);
		}
	};

	const renderContent = () => {
		return (
			<>
				<LoadingAnimation isLoading={isLoading} />
				<div className="row">
					<div className="d-flex flex-row justify-content-between mb-3">
						<div>
							{originLocation.id ? (
								<>
									<h1>{t('standingData:EDIT_ORIGIN')}</h1>
								</>
							) : (
								<>
									<h1>{t('standingData:NEW_ORIGIN')}</h1>
								</>
							)}
						</div>
						<div>
							<BtnSave
								handleSave={() => handleSaveOriginLocation()}
								isLoading={isLoadingSave}
							/>
						</div>
					</div>
				</div>
				<div className="row">
					<div className="col-md-4 col-xl-3 col-lg-3 col-sm-4 col-12">
						<label>{t('_general:CODE')}</label>
						<input
							className="form-control"
							error-key={'Code'}
							name="code"
							onChange={handleCodeValueChange}
							type="text"
							value={originLocation.code}
						/>
					</div>
					<div className="col-mg-8 col-xl-9 col-sm-8 col-12">
						<LanguageSwitch
							handleSelectLanguage={handleSelectLanguage}
							selectedLanguage={selectedCultureCode}
							translatable={true}
							onTranslateClick={handleTranslate}
							entryLanguage={reduxCultureCode}
							content={
								<OriginLocationLanguageInput
									origin={originLocation}
									selectedLanguage={selectedCultureCode}
									handleValueChange={handleInputValueChange}
								/>
							}
						/>
					</div>
				</div>
			</>
		);
	};

	return <>{renderContent()}</>;
};

export default OriginLocationDetailComponent;
